// Admin认证服务
export interface AdminCredentials {
  email: string;
  password: string;
}

export interface AdminUser {
  email: string;
  isAdmin: boolean;
}

interface LoginResponse {
  success: boolean;
  user?: AdminUser;
  token?: string;
  error?: string;
}

class AdminService {
  private readonly ADMIN_SESSION_KEY = 'reddit_admin_session';
  private readonly ADMIN_TOKEN_KEY = 'adminToken';

  // 验证admin登录
  async login(credentials: AdminCredentials): Promise<boolean> {
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data: LoginResponse = await response.json();

      if (data.success && data.user && data.token) {
        // 保存登录状态和token
        localStorage.setItem(this.ADMIN_SESSION_KEY, JSON.stringify(data.user));
        localStorage.setItem(this.ADMIN_TOKEN_KEY, data.token);
      return true;
      } else {
        console.error('Login failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Login request failed:', error);
      return false;
    }
  }

  // 退出登录
  async logout(): Promise<void> {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`
        }
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
    localStorage.removeItem(this.ADMIN_SESSION_KEY);
      localStorage.removeItem(this.ADMIN_TOKEN_KEY);
    }
  }

  // 检查是否已登录admin
  isLoggedIn(): boolean {
    try {
      const stored = localStorage.getItem(this.ADMIN_SESSION_KEY);
      const token = localStorage.getItem(this.ADMIN_TOKEN_KEY);
      
      if (!stored || !token) return false;
      
      const adminUser: AdminUser = JSON.parse(stored);
      return adminUser.isAdmin;
    } catch {
      return false;
    }
  }

  // 获取token
  getToken(): string | null {
    return localStorage.getItem(this.ADMIN_TOKEN_KEY);
  }

  // 获取当前admin用户信息
  getCurrentUser(): AdminUser | null {
    try {
      const stored = localStorage.getItem(this.ADMIN_SESSION_KEY);
      if (!stored) return null;
      
      const adminUser: AdminUser = JSON.parse(stored);
      if (adminUser.isAdmin) {
        return adminUser;
      }
      return null;
    } catch {
      return null;
    }
  }

  // 验证admin权限（从服务端验证）
  async checkAdminStatus(): Promise<boolean> {
    try {
      const token = this.getToken();
      if (!token) return false;

      const response = await fetch('/api/admin/check', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.isAdmin && data.user) {
        // 更新本地存储的用户信息
        localStorage.setItem(this.ADMIN_SESSION_KEY, JSON.stringify(data.user));
        return true;
      } else {
        // 清除无效的登录状态
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      this.logout();
      return false;
    }
  }

  // 验证admin权限
  requireAdmin(): boolean {
    return this.isLoggedIn();
  }
}

export const adminService = new AdminService(); 